import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, Globe, MapPin, Send, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

import StarField from "../components/StarField";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { COMPANY } from "../mock/mockData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message sent!", { description: "We'll get back within 2 business hours." });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Please try again or call 0424 497 770.";
      toast.error("Something went wrong", { description: detail });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative" data-testid="contact-page">
      <section className="relative py-28 md:py-36 overflow-hidden">
        <StarField count={50} />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,165,116,0.12),transparent_60%)]" />
        <div className="container-lux relative z-10 max-w-4xl fade-up">
          <div className="divider-gold mb-6">Let's Chat</div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] mb-8">
            Reach out - <em className="text-gold not-italic">we love a good hello.</em>
          </h1>
          <p className="text-lg text-white/70 max-w-2xl leading-relaxed">
            Whether you have a question, want a quote, or just need someone to listen - drop us a line. We answer every message personally.
          </p>
        </div>
      </section>

      <section className="pb-28 md:pb-36">
        <div className="container-lux grid lg:grid-cols-12 gap-12">
          {/* Info */}
          <div className="lg:col-span-5 space-y-4" data-testid="contact-info">
            {[
              { Icon: Phone, label: "Call us", value: COMPANY.phone, href: `tel:${COMPANY.phone.replace(/\s/g, "")}` },
              { Icon: Mail, label: "Email us", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
              { Icon: Globe, label: "Online", value: COMPANY.website, href: `https://${COMPANY.website}` },
              { Icon: MapPin, label: "Location", value: COMPANY.address, href: null },
            ].map(({ Icon, label, value, href }) => (
              <a
                key={label}
                href={href || "#"}
                className="lux-card rounded-2xl p-6 flex items-start gap-5 group"
                data-testid={`contact-info-${label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className="w-12 h-12 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center flex-shrink-0 group-hover:bg-gold/20 transition-colors">
                  <Icon className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase text-gold mb-1">{label}</div>
                  <div className="text-white group-hover:text-gold transition-colors break-all">{value}</div>
                </div>
              </a>
            ))}

            <div className="lux-card rounded-2xl p-6">
              <div className="text-xs tracking-[0.2em] uppercase text-gold mb-3">Office Hours</div>
              <div className="space-y-1.5 text-sm text-white/70">
                <div className="flex justify-between"><span>Monday – Friday</span><span className="text-white">7am – 7pm</span></div>
                <div className="flex justify-between"><span>Saturday</span><span className="text-white">9am – 5pm</span></div>
                <div className="flex justify-between"><span>Sunday</span><span className="text-white">On-call only</span></div>
              </div>
              <div className="mt-4 pt-4 border-t border-white/5 text-xs text-gold">
                Urgent support? Call us anytime - we're available 24/7 for current participants.
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <div className="lux-card rounded-[2rem] p-8 md:p-12" data-testid="contact-form-card">
              {sent ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center mx-auto mb-5 glow-pulse">
                    <Check className="w-8 h-8 text-gold" />
                  </div>
                  <h3 className="font-serif text-3xl mb-3">Message received.</h3>
                  <p className="text-white/70 mb-6">Thank you, {form.name.split(" ")[0]}. We'll be in touch shortly.</p>
                  <button
                    onClick={() => {
                      setSent(false);
                      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
                    }}
                    className="text-xs tracking-[0.15em] uppercase text-gold link-gold"
                    data-testid="contact-new-message-btn"
                  >
                    Send another
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <h2 className="font-serif text-3xl md:text-4xl mb-2">Send us a message</h2>
                    <p className="text-white/60 text-sm">We reply to every enquiry within 2 business hours.</p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <Label className="text-xs tracking-[0.2em] uppercase text-gold mb-2 block">Name</Label>
                      <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-deep-surface border-white/10 text-white input-lux" data-testid="contact-name" />
                    </div>
                    <div>
                      <Label className="text-xs tracking-[0.2em] uppercase text-gold mb-2 block">Email</Label>
                      <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-deep-surface border-white/10 text-white input-lux" data-testid="contact-email" />
                    </div>
                    <div>
                      <Label className="text-xs tracking-[0.2em] uppercase text-gold mb-2 block">Phone</Label>
                      <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-deep-surface border-white/10 text-white input-lux" data-testid="contact-phone" />
                    </div>
                    <div>
                      <Label className="text-xs tracking-[0.2em] uppercase text-gold mb-2 block">Subject</Label>
                      <Select value={form.subject} onValueChange={(v) => setForm({ ...form, subject: v })}>
                        <SelectTrigger className="bg-deep-surface border-white/10 text-white" data-testid="contact-subject">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent className="bg-deep-elevated border-white/10 text-white">
                          <SelectItem value="general">General Enquiry</SelectItem>
                          <SelectItem value="services">Services Question</SelectItem>
                          <SelectItem value="ndis">NDIS Support</SelectItem>
                          <SelectItem value="careers">Careers</SelectItem>
                          <SelectItem value="partner">Partnership</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase text-gold mb-2 block">Message</Label>
                    <Textarea
                      required
                      rows={5}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Tell us a little about what you need..."
                      className="bg-deep-surface border-white/10 text-white input-lux resize-none"
                      data-testid="contact-message"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-gold w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold tracking-[0.12em] uppercase text-deep hover:bg-gold-light transition-all disabled:opacity-60"
                    data-testid="contact-submit"
                  >
                    {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <><Send className="w-4 h-4" /> Send Message</>}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
